/**
 * Category bar for collection pages.
 *
 * The chips are plain filter links, so the bar works without JavaScript. This
 * component upgrades them to the same in-place update the filters drawer uses
 * (`facets-form-component.updateFiltersByURL`), and keeps the active chip
 * visible inside the horizontal scroller.
 */
class CategoryBar extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    // The section is morphed after every filter change, so re-centre the active chip.
    requestAnimationFrame(() => this.#revealActive());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = /** @type {HTMLElement} */ (event.target).closest?.('a.category-bar__link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return; // fall back to navigation

    event.preventDefault();

    // Optimistic state while the section re-renders
    this.querySelectorAll('.category-bar__link--active').forEach((el) => {
      el.classList.remove('category-bar__link--active');
      el.removeAttribute('aria-current');
    });
    link.classList.add('category-bar__link--active');
    link.setAttribute('aria-current', 'true');

    facetsForm.updateFiltersByURL(link.href);
  };

  /**
   * @returns {any}
   */
  #getFacetsForm() {
    const section = this.closest('.shopify-section') ?? document;
    return section.querySelector('facets-form-component') ?? document.querySelector('facets-form-component');
  }

  #revealActive() {
    const scroller = this.querySelector('.category-bar__scroller');
    const active = this.querySelector('.category-bar__link--active');
    if (!(scroller instanceof HTMLElement) || !(active instanceof HTMLElement)) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const fullyVisible = activeRect.left >= scrollerRect.left && activeRect.right <= scrollerRect.right;
    if (fullyVisible) return;

    scroller.scrollTo({
      left: scroller.scrollLeft + activeRect.left - scrollerRect.left - 16,
      behavior: 'instant',
    });
  }
}

if (!customElements.get('category-bar')) {
  customElements.define('category-bar', CategoryBar);
}
